import setTitleAndMetaDescription from "../../components/setTitleAndMetaDescription.tsx";
import useDatalayer from "../../components/useDatalayer.tsx";
import OrderAddressSearchComponent from "../../components/order/OrderAddressSearchComponent.tsx";
import Icon from "../../components/selfcare/Icon.tsx";
import CallMeRTOComponent from "../../components/callMe/CallMeRTOComponent.tsx";
import {BigCoverMapRegions, FiberCoverMapRegions} from "../../components/coverageMap/CoverageMap.ts";
import PriceListItemComponent, {PriceListTDCFiberComponent} from "../../components/broadband/PriceListItemComponent.tsx";
import {useEffect, useMemo, useState} from "react";
import {AdjustmentsListPrices, getPriceList, ProductListPriceProviders} from "../../util/request.ts";
import {Link} from "react-router-dom";
import CoverageMapContainer from "../../components/coverageMap/CoverageMapContainer.tsx";
import QuestionAnswerComponent, {Question} from "../../components/help/QuestionAnswerComponent.tsx";
import PriceListAdjustmentComponent from "../../components/broadband/PriceListAdjustmentComponent.tsx";
import ProductListComponent from "../../components/broadband/ProductListComponent.tsx";
import {formatAmountWithCurrencyOverPeriod} from "../../util/formatter.ts";
import ScrollToTop from "../../components/ScrollToTop.tsx";
import {DispatchHighLightAddressSearchEvent} from "../../util/events.ts";
import LoaderSpinner from "../../components/loaders/LoaderSpinner.tsx";
import Routing from "../../Routing.ts";
import CallMeFloatingComponent from "../../components/callMe/CallMeFloatingComponent.tsx";
import ChatBotBoxComponent from "../../components/ChatBotBoxComponent.tsx";
import IconLookingGlass from "../../components/icons/IconLookingGlass.tsx";
import IconDocument from "../../components/icons/IconDocument.tsx";
import IconRouterSmall from "../../components/icons/IconRouterSmall.tsx";

const questions: Question[] = [
    {
        question: 'Hvad er bredbånd?',
        answer: <p>
            Bredbånd er en type dataoverførsel, der kan sende og modtage større mængder data.
            En teknologi, der giver højhastighedsadgang til internettet, som gør det muligt at streame videoer, downloade filer, spille online spil og andre online tjenester.
            Bredbånd er hurtigere og mere stabilt end forbindelser via telefonstik.
        </p>,
    },
    {
        question: 'Hvad er forskellen på fibernet bredbånd og andre typer bredbånd?',
        answer: <>
            <p>
                Der er forskellige typer bredbåndsforbindelser at vælge imellem og de har alle forskellige hastigheder, teknologi og egenskaber alt efter behov
                Vi tilbyder bredbånd via <Link to={Routing.Broadband.Fiber}>fiber</Link>, <Link to={Routing.Broadband.TDC5G}>5G</Link> og <Link
                to={Routing.Broadband.Coax}
            >kabel-tv-stikket</Link>.
            </p>
            <p>
                Alle tre slags forbindelser kan trække 1000 Mbit i download, men de kan nå forskellige hastigheder i upload. Den eneste forbindelse som kan trække 1000/1000 Mbit er fibernet, som er det bedste, hurtigste og mest stabile bredbånd.
                Hvis du vil sammenligne de forskellige bredbåndsforbindelser med hinanden, så læs:
            </p>
            <p className="align-center">
                <Link
                    to="/hjaelp/internet-og-wifi/hvilket-internet-skal-jeg-vaelge"
                    className="button button--montserrat teal"
                >Hvilket internet skal jeg vælge?</Link>
            </p>
        </>,
    },
    {
        question: 'Hvad koster bredbånd?',
        answer: <>
            <p>
                Du kan se dine muligheder af bredbåndsforbindelser samt bredbåndspriser, når du laver et adresseopslag.
                Priserne på vores bredbånd afhænger nemlig af din adresse.
                Vi har ofte tilbud på vores bredbånd, så tjek om din adresse kan få billigt bredbånd.
            </p>
            <p className="align-center">
                <button
                    className={'button button--montserrat teal'}
                    onClick={() => DispatchHighLightAddressSearchEvent(true)}
                >Tjek adresse her</button>
            </p>
        </>,
    },
    {
        question: 'Hvilket bredbånd kan jeg få?',
        answer: <>
            <p>
                Den type bredbåndsforbindelse du kan få, afhænger af installationen på din adresse. <button
                    className={'link'}
                    onClick={() => DispatchHighLightAddressSearchEvent(true)}
                >Lav et adresseopslag</button> for at se, hvilke muligheder og bredbåndstilbud, du kan få.
                Vi anbefaler altid <Link to={Routing.Broadband.Fiber}>bredbånd via fiber</Link>, da det er en fremtidssikring af dit internet samt det bedste og hurtigste bredbånd.
                Men med 5G-internet får du Danmarks bedste 5G-dækning samt en nem installation.
                Så typen af bredbåndsforbindelse, som du vælger, afhænger af dine behov.
            </p>
            <p className="align-center">
                <button
                    className={'button button--montserrat teal'}
                    onClick={() => DispatchHighLightAddressSearchEvent(true)}
                >Tjek adresse her</button>
            </p>
        </>,
    },
    {
        question: 'Hvor hurtigt bredbånd kan jeg få?',
        answer: <>
            <p>
                Hvor hurtig en bredbåndsforbindelse du kan få, afhænger af din adresse samt fysiske faktorer i dit hjem. <button
                    className={'link'}
                    onClick={() => DispatchHighLightAddressSearchEvent(true)}
                >Slå din adresse op</button> for at se, hvilken hastighed du kan få.
                Bredbånd via fiber er det hurtigste bredbånd på markedet.
            </p>
            <p>
                Hvis du vil have det optimale ud af din hastighed og wifi-oplevelse, spiller <Link to="/hjaelp/internet-og-wifi/placering-af-router">placeringen af din router</Link> en stor rolle.
                Andre materialer i din bolig kan også <Link to="/hjaelp/internet-og-wifi/hvordan-traekker-jeg-1000-mbit">forstyrre signalet</Link>.
                Følg vores råd i artiklerne, så du kan få det bedste ud af dit internet.
            </p>
        </>,
    },
    {
        question: 'Er der sikkerhedspakker inkluderet i mit bredbåndsabonnement?',
        answer: <>
            <p>
                Hos Hiper sælger vi kun internet. Det betyder også, at der ikke er sikkerhedspakker eller anden form for antivirus,
                inkluderet i nogen af vores bredbåndsløsninger.
            </p>
            <p>
                I mange tilfælde er det slet ikke nødvendigt med en sikkerhedspakke,
                da de fleste computere og enheder i dag allerede har et tilstrækkeligt antivirusprogram installeret.
            </p>
        </>,
    },
]

export default function BroadbandIndexPage() {
    ScrollToTop()
    useDatalayer()
    setTitleAndMetaDescription('Bredbånd fra 99,-/md. | Hurtig og billig bredbåndsforbindelse', 'Hiper har knivskarpe bredbåndspriser, så køb dit nye bredbåndsabonnement online allerede i dag, så du kan få lynhurtigt internet.')

    const [productList, setProductList] = useState<ProductListPriceProviders>();
    const [adjustmentList, setAdjustmentList] = useState<AdjustmentsListPrices>();

    useEffect(() => {
        getPriceList().then((r) => {
            setProductList(r.response.products)
            setAdjustmentList(r.response.adjustments)
        })
    }, [])

    const mbbProduct = useMemo(() => {
        if (productList === undefined || productList.mbb === undefined) {
            return false
        }

        return productList.mbb[0]
    }, [productList])

    return (
        <div className={"page-products"}>
            <section className="content content--narrow">
                <div className="products-hero">
                    <h1
                        className={'heading heading--blue heading--bigger'}
                        onClick={() => DispatchHighLightAddressSearchEvent(true)}
                    >
                        Stabilt og lyn&shy;hurtigt bredbånd
                    </h1>
                    <p>
                        Her er vores bedste bredbånd via fibernet, 5G og kabel-tv.
                        Tjek din adresse og se hvilke bredbåndsforbindelser og tilhørende bredbåndspriser, som du specifikt kan få.
                    </p>
                    <OrderAddressSearchComponent/>
                </div>
            </section>

            <section className="content content--narrow subheader">
                <h2 className="heading heading--big">
                    Tilbud på bredbånd lige&nbsp;nu!
                </h2>
                <p>Trådløs wifi lånerouter inkluderet i dit bredbåndsabonnement</p>
            </section>

            <ProductListComponent/>

            <section className="content content--wide section-white subheader">
                <div className="header-steps">
                    <h2 className="heading heading--blue heading--big">
                        Så nemt er det at få billigt og hurtigt bredbånd
                    </h2>
                    <p>
                        Hos Hiper har vi gjort det nemt for alle at få lynhurtigt og billigt internet &ndash; lige fra bestilling af nyt til opsigelse af gammel bredbåndsudbyder.
                    </p>
                </div>
                <div className="grid--steps">
                    <div className="grid--item">
                        <span className="number">1</span>
                        <Icon component={IconLookingGlass}/>
                        <h3 className="heading heading--paragraph heading--small">
                            <span>Indtast din adresse</span>
                            <span>og bestil bredbånd</span>
                        </h3>
                    </div>

                    <div className="grid--item">
                        <span className="number">2</span>
                        <Icon component={IconDocument}/>
                        <h3 className="heading heading--paragraph heading--small">
                            <span>Vi hjælper med at opsige</span>
                            <span>din gamle bredbåndsudbyder</span>
                        </h3>
                    </div>

                    <div className="grid--item">
                        <span className="number">3</span>
                        <Icon component={IconRouterSmall}/>
                        <h3 className="heading heading--paragraph heading--small">
                            <span>Du modtager dit bredbåndsudstyr</span>
                            <span>&ndash; og så er du i gang med hurtigt bredbånd</span>
                        </h3>
                    </div>
                </div>
            </section>

            <CallMeRTOComponent className={"rto--section page-products"}/>

            <section className="prices">
                <div className="content content--narrow">
                    <div className="blurb">
                        <h2 className="heading heading--big">
                            Bredbåndspriser
                        </h2>
                        <h3 className="heading heading--paragraph heading--small">
                            Herunder finder du priser på vores bredbånd via fibernet, 5G og kabel-tv-stik.
                            Alle bredbåndspriser er inklusive moms, så tjek om du kan få billigt bredbånd nu.
                        </h3>
                    </div>

                    <section className="accordion-container">
                        <h3 className="heading heading--left heading--medium">Bredbånd via fibernet fra de forskellige netværker</h3>
                        {productList === undefined && (
                            <LoaderSpinner/>
                        )}
                        {productList !== undefined && (
                            <>

                                {productList.tdc !== undefined && (
                                    <PriceListTDCFiberComponent products={productList.tdc}/>
                                )}

                                {productList.e !== undefined && (
                                    <PriceListItemComponent
                                        region={FiberCoverMapRegions.FIBER_E}
                                        title={"EWII fiber"}
                                        products={productList.e}
                                    />
                                )}

                                {productList.n !== undefined && (
                                    <PriceListItemComponent
                                        region={FiberCoverMapRegions.FIBER_N}
                                            title={"Norlys fiber"}
                                            products={productList.n}
                                    />
                                )}

                                {productList.ne !== undefined && (
                                    <PriceListItemComponent
                                        region={FiberCoverMapRegions.FIBER_NE}
                                        title={"Nord Energi fiber"}
                                        products={productList.ne}
                                    />
                                )}

                                {productList.f !== undefined && (
                                    <PriceListItemComponent
                                        region={FiberCoverMapRegions.FIBER_F}
                                        title={"Fibia fiber"}
                                        products={productList.f}
                                    />
                                )}

                                {productList.ef !== undefined && (
                                    <PriceListItemComponent
                                        region={FiberCoverMapRegions.FIBER_EF}
                                        title={"Energi Fyn fiber"}
                                        products={productList.ef}
                                    />
                                )}

                                {productList.tm !== undefined && (
                                    <PriceListItemComponent
                                        region={FiberCoverMapRegions.FIBER_TM}
                                        title={"Thy-Mors Energi fiber"}
                                        products={productList.tm}
                                    />
                                )}

                                {productList.gc !== undefined && (
                                    <PriceListItemComponent
                                        region={FiberCoverMapRegions.FIBER_GC}
                                        title={"GlobalConnect fiber"}
                                        products={productList.gc}
                                    />
                                )}

                                {productList.sef !== undefined && (
                                    <PriceListItemComponent
                                        region={FiberCoverMapRegions.FIBER_SEF}
                                        title={"SEF fiber"}
                                        products={productList.sef}
                                    />
                                )}

                                {productList.rah !== undefined && (
                                    <PriceListItemComponent
                                        region={FiberCoverMapRegions.FIBER_RAH}
                                        title={"RAH fiber"}
                                        products={productList.rah}
                                    />
                                )}
                            </>
                        )}
                    </section>


                    <section>
                        <h3 className="heading heading--left heading--medium">5G-internet</h3>
                        <div className="prices-container">
                            <div className="coverage-map-wrapper coverage-map-wrapper--mbb">
                                <p>
                                    Se område og pris på <Link to={Routing.Broadband.TDC5G}>5G-internet</Link>
                                </p>
                                <iframe
                                    aria-label="5G-internet dækningskort"
                                    height=""
                                    width="100%"
                                    src="//daekning.tdc.dk/tdcnetmap_ext_tile2/?tema=all_speed_outdoor_200Mbit_MapTiler"
                                ></iframe>
                            </div>
                            <table cellSpacing="0" className="prices-table">
                                <tbody>
                                <tr>
                                    <td>Op til 1.000/100 Mbit</td>
                                    <td>{mbbProduct ? formatAmountWithCurrencyOverPeriod(mbbProduct.price) : '-'}</td>
                                </tr>
                                <tr>
                                    <td className="prices-table-address-button" colSpan={2}>
                                        <p>
                                            Vi har ofte bredbånd på tilbud, slå din adresse op for at se, hvad vi kan levere til dig af hastighed og pris på din bredbåndsforbindelse:
                                        </p>
                                        <button
                                            className={'button button--montserrat rounded'}
                                            onClick={() => DispatchHighLightAddressSearchEvent(true)}
                                        >
                                            Tjek prisen på din adresse
                                        </button>
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </div>
                    </section>

                    <hr className="price-divider"/>

                    <section>
                        <h3 className="heading heading--left heading--medium">Bredbånd via kabel-tv-stik</h3>
                        <div className="prices-container">
                            <div className="coverage-map-wrapper">
                                <p>
                                    Se område og pris på <Link to={Routing.Broadband.Coax}>bredbånd via kabel-tv-stik</Link>
                                </p>
                                <CoverageMapContainer regionPreset={BigCoverMapRegions.COAX} fiberMapOnly={false} static={true}/>
                            </div>

                            <table cellSpacing="0" className="prices-table">
                                <tbody>
                                {productList !== undefined && (
                                    (productList.coax.map((p, i) => {
                                        return (
                                            <tr key={i}>
                                                <td>{p.line_speed_down}/{p.line_speed_up} Mbit</td>
                                                <td>Fra {formatAmountWithCurrencyOverPeriod(p.price)}</td>
                                            </tr>
                                        )
                                    }))
                                )}
                                <tr>
                                    <td className="prices-table-address-button" colSpan={2}>
                                        <p>
                                            Vi har ofte bredbånd på tilbud, slå din adresse op for at se, hvad vi kan levere til dig af hastighed og pris på din bredbåndsforbindelse:
                                        </p>
                                        <button
                                            className={'button button--montserrat rounded'}
                                            onClick={() => DispatchHighLightAddressSearchEvent(true)}
                                        >
                                            Tjek prisen på din adresse
                                        </button>
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </div>
                    </section>

                    <hr className="price-divider"/>

                </div>
            </section>

            <section className="adjustments">
                <div className="content content--narrow">
                    {adjustmentList !== undefined && (
                        <PriceListAdjustmentComponent adjustments={adjustmentList}/>
                    )}
                </div>
            </section>

            <section className="section-white faq">
                <div className="content">
                    <div className="blurb">
                        <h2 className="heading heading--big">Værd at vide om bredbånd </h2>
                    </div>
                    <QuestionAnswerComponent questions={questions}/>

                    <div className="blurb">
                        <p>
                            <button
                                className={'button button--montserrat big rounded'}
                                onClick={() => DispatchHighLightAddressSearchEvent(true)}
                            >
                                Køb dit bredbånd nu
                            </button>
                        </p>
                        <p>
                            Vi elsker tilfredse kunder &ndash; og vi vil rigtig gerne være din nye bredbåndsudbyder.
                        </p>
                    </div>
                </div>
            </section>

            <hr/>

            <section className="conditions">
                <div className="content content--centered">
                    <div className="blurb">
                        <h2 className="heading heading--big">Aftalevilkår</h2>
                    </div>
                    <p>
                        <Link to={Routing.About.Conditions} className="button button--montserrat big rounded">
                            Se vores aftalevilkår
                        </Link>
                    </p>
                </div>
            </section>

            <CallMeFloatingComponent/>
            <ChatBotBoxComponent />
        </div>
    )
}
